import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { useCallback } from 'react';
import type { UseBoundStore } from 'zustand/react';
import type { StoreApi } from 'zustand/vanilla';

import type {
  AIWorkflowJobResult,
  AIWorkflowState,
} from '@/components/ai-workflows/aiWorkflowStore';
import { handleError } from '@/utils/errorUtils';

export function buildAiWorkflowService<TState>(
  store: UseBoundStore<StoreApi<AIWorkflowState<TState>>>,
  apiEndpoint: string,
  retryInterval: number
) {
  return () => {
    const { getAccessTokenSilently, isAuthenticated } = useRisksmartUser();

    function getWorkflowApiUrl(): string {
      const aiWorkflowApiUrl = getEnv('REACT_APP_AI_WORKFLOW_API_URL');

      if (!aiWorkflowApiUrl) {
        throw new Error('Unable to find the URL for the workflow API');
      }

      return aiWorkflowApiUrl;
    }

    async function checkJob(
      token: string,
      aiWorkflowApiUrl: string,
      location: string,
      resultProcessor: (result: AIWorkflowJobResult<TState>) => void
    ): Promise<AIWorkflowJobResult<TState>> {
      const result = await fetch(`${aiWorkflowApiUrl}${location}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Response-Case': 'camel',
        },
      });

      if (!result.ok) {
        throw new Error(
          `Something went wrong trying to get the current status of the workflow job. Error: ${await result.text()}`
        );
      }

      const response = await result.json();

      resultProcessor(response);

      return response;
    }

    async function startWorkflow(
      aiWorkflowApiUrl: string,
      token: string,
      bodyBuilder: () => unknown,
      resultProcessor: (result: AIWorkflowJobResult<TState>) => void
    ): Promise<AIWorkflowJobResult<TState>> {
      const result = await fetch(
        `${aiWorkflowApiUrl}/ai-engine/workflow-api/workflows/${apiEndpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Response-Case': 'camel',
          },
          body: JSON.stringify(bodyBuilder()),
        }
      );

      if (!result.ok) {
        throw new Error(
          `Encountered an error whilst trying to start the workflow. Error: ${await result.text()}`
        );
      }

      const response = await result.json();

      resultProcessor(response);

      return response;
    }

    const runWorkflow = useCallback(
      async (
        requestBodyBuilder: () => unknown,
        resultProcessor: (result: AIWorkflowJobResult<TState>) => void
      ) => {
        async function getAccessToken(): Promise<string> {
          if (!isAuthenticated) {
            throw new Error(
              'Attempting to run the workflow, but the user is not authenticated'
            );
          }

          return await getAccessTokenSilently();
        }

        function processResult(
          result: AIWorkflowJobResult<TState>,
          jobId: string
        ) {
          if (result.status === 'completed') {
            store.getState().setResults(result);
          } else if (result.status === 'failed') {
            store.getState().setHasErrored();

            throw new Error(
              result.error ??
                `Encountered an error whilst processing the workflow job (${jobId}), but no reason was provided`
            );
          }
        }

        function pollForResults(
          workflowApiUrl: string,
          job: AIWorkflowJobResult<TState>
        ) {
          let retries = 0;

          const intervalId = setInterval(async () => {
            if (retries > 10) {
              store.getState().setHasTimedOut();
            }

            if (!store.getState().isJobInProgress) {
              clearInterval(intervalId);

              return;
            }

            try {
              const result = await checkJob(
                await getAccessTokenSilently(),
                workflowApiUrl,
                job.location,
                resultProcessor
              );

              processResult(result, job.jobId);
            } catch (e) {
              store.getState().setHasErrored();

              handleError(e);
            }

            retries += 1;
          }, retryInterval);
        }

        try {
          const token = await getAccessToken();

          const aiWorkflowApiUrl = getWorkflowApiUrl();

          store.getState().reset();

          const response = await startWorkflow(
            aiWorkflowApiUrl,
            token,
            requestBodyBuilder,
            resultProcessor
          );

          if (response.status === 'completed') {
            store.getState().setResults(response);
          } else {
            store.getState().jobStarted(response.jobId);

            pollForResults(aiWorkflowApiUrl, response);
          }
        } catch (e) {
          store.getState().setHasErrored();

          handleError(e);
        }
      },
      [getAccessTokenSilently, isAuthenticated]
    );

    return {
      runWorkflow,
    };
  };
}
